// ─── Brand rail: siftable grocery-brand tiles ───────────────────────────────
// Initial-based SVG tiles in brand-ish colors (NOT trademarked logos —
// two-letter monograms on muted brand-adjacent hues). Tapping a brand
// filters the Weekly tab's deals to that store; tapping again clears.
// "All" is the default.
//
// Monograms are two letters so colliding initials (Superstore/Safeway/Sobeys)
// stay distinct: RC / CK / SW / SB / CP / TT / NF / FC.
import React from "react";
import { T } from "./components";

export const BRANDS = [
  { id: "superstore", label: "Superstore", mono: "RC", color: "#E63B2E", match: ["superstore", "real canadian"] },
  { id: "costco", label: "Costco", mono: "CK", color: "#1D7FD1", match: ["costco"] },
  { id: "safeway", label: "Safeway", mono: "SW", color: "#FF7A1A", match: ["safeway"] },
  { id: "sobeys", label: "Sobeys", mono: "SB", color: "#37B24D", match: ["sobeys"] },
  { id: "coop", label: "Co-op", mono: "CP", color: "#8B5CC4", match: ["co-op", "coop"] },
  { id: "tt", label: "T&T", mono: "TT", color: "#F0567C", match: ["t&t"] },
  { id: "nofrills", label: "No Frills", mono: "NF", color: "#FFB800", match: ["no frills"] },
  { id: "freshco", label: "FreshCo", mono: "FC", color: "#12B5A5", match: ["freshco"] },
  { id: "saveonfoods", label: "Save-On-Foods", mono: "SO", color: "#66BB2E", match: ["save-on", "save on"] },
  { id: "walmart", label: "Walmart", mono: "WM", color: "#3B82F6", match: ["walmart"] },
];

export function brandIdFor(storeName) {
  const s = (storeName || "").toLowerCase();
  const b = BRANDS.find((b) => b.match.some((m) => s.includes(m)));
  return b ? b.id : null;
}

export function BrandRail({ active, onPick }) {
  return (
    <div style={styles.rail} role="tablist" aria-label="Filter by store brand">
      {BRANDS.map((b) => {
        const isActive = active === b.id;
        return (
          <button
            key={b.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onPick(isActive ? null : b.id)}
            style={styles.tile(isActive)}
          >
            <span style={styles.mono(b.color, isActive)}>{b.mono}</span>
            <span style={styles.label(isActive)}>{b.label}</span>
          </button>
        );
      })}
    </div>
  );
}

const styles = {
  rail: {
    display: "flex", gap: 10, overflowX: "auto",
    padding: "2px 2px 10px", marginBottom: 4,
    scrollbarWidth: "none",
    // v2.2: momentum scrolling with snap — tiles stay swipeable and land clean.
    WebkitOverflowScrolling: "touch", scrollSnapType: "x proximity",
  },
  tile: (active) => ({
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    background: "transparent", border: "none", cursor: "pointer",
    padding: 2, flexShrink: 0, fontFamily: "inherit",
    opacity: 1, transform: active ? "scale(1.04)" : "scale(1)",
    scrollSnapAlign: "start",
    transition: "transform 0.24s cubic-bezier(0.22,1,0.36,1)",
  }),
  mono: (color, active) => ({
    width: 58, height: 58, borderRadius: 19,
    // v3.8: flyer style — vibrant solid tile, white monogram. Selected
    // stays one confident black tile.
    background: active ? "#141210" : color,
    // yellow tile (No Frills) needs dark type for contrast
    color: !active && color === "#FFB800" ? "#141210" : "#fff",
    border: `2.5px solid ${active ? "#141210" : color}`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 19, fontWeight: 900, letterSpacing: 0,
    boxShadow: active ? "0 6px 16px rgba(20,18,16,0.28)" : "0 3px 8px rgba(0,0,0,0.16)",
    transition: "background 0.24s cubic-bezier(0.22,1,0.36,1), color 0.24s cubic-bezier(0.22,1,0.36,1), border-color 0.24s cubic-bezier(0.22,1,0.36,1), box-shadow 0.24s cubic-bezier(0.22,1,0.36,1)",
  }),
  label: (active) => ({
    fontSize: 11, fontWeight: active ? 800 : 700,
    color: active ? T.text : T.textSub, whiteSpace: "nowrap",
  }),
};
